import { Link } from '@tanstack/react-router'
import { ColumnDef } from '@tanstack/react-table'
import { useTranslation } from '@/hooks/use-translation'
import { Badge } from '@/components/ui/badge'
import { useFnsMode } from '@/features/forms-and-surveys/hooks/use-fns-mode'
import { Survey, SurveyType } from '@/features/forms-and-surveys/types'
import {
  getSurveyTypeColor,
  getTranslatedSurveyTypeLabel,
} from '@/features/forms-and-surveys/utils'
import { FnsActionsCell } from './cell/fns-actions-cell'

export function useFnsColumns(): ColumnDef<Survey>[] {
  const mode = useFnsMode()
  const { t } = useTranslation()

  const baseColumns: ColumnDef<Survey>[] = [
    {
      accessorKey: 'title',
      header: t('common.name'),
      cell: ({ row }) => {
        const survey = row.original
        return (
          <Link
            to={mode === 'survey' ? '/surveys/$surveyId' : '/forms/$formId'}
            params={
              mode === 'survey'
                ? { surveyId: survey.id }
                : { formId: survey.id }
            }
            className='font-medium text-primary hover:underline'
          >
            {row.getValue('title')}
          </Link>
        )
      },
      meta: {
        className: 'w-64 pl-4',
      },
    },
  ]

  // Only include type column when mode is 'survey'
  const typeColumn: ColumnDef<Survey> = {
    accessorKey: 'type',
    header: t('common.type'),
    cell: ({ row }) => {
      const type = row.getValue('type') as SurveyType
      return (
        <Badge className={getSurveyTypeColor(type)}>
          {getTranslatedSurveyTypeLabel(t, type)}
        </Badge>
      )
    },
    meta: {
      className: 'w-32',
    },
  }

  const remainingColumns: ColumnDef<Survey>[] = [
    {
      accessorKey: 'isActive',
      header: t('common.status'),
      cell: ({ row }) => {
        const isActive = row.getValue('isActive') as boolean
        return (
          <Badge
            variant={isActive ? 'default' : 'secondary'}
            className={
              isActive
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                : ''
            }
          >
            {isActive ? t('common.active') : t('common.inactive')}
          </Badge>
        )
      },
      meta: {
        className: 'w-24',
      },
    },
    {
      accessorKey: 'isAnonymous',
      header: t('surveys.privacy'),
      cell: ({ row }) => {
        const isAnonymous = row.getValue('isAnonymous') as boolean
        return (
          <Badge variant={isAnonymous ? 'outline' : 'secondary'}>
            {isAnonymous ? t('surveys.anonymous') : t('surveys.authenticated')}
          </Badge>
        )
      },
      meta: {
        className: 'w-32',
      },
    },
    {
      accessorKey: 'questions',
      header: t('surveys.questions'),
      cell: ({ row }) => {
        const questions = row.getValue('questions') as Survey['questions']
        return <div className='text-sm'>{questions?.length || 0}</div>
      },
      meta: {
        className: 'w-20',
      },
    },
    {
      accessorKey: 'createdAt',
      header: t('common.createdAt'),
      cell: ({ row }) => {
        const date = row.getValue('createdAt') as string
        return (
          <div className='text-sm'>
            {date ? new Date(date).toLocaleDateString() : '-'}
          </div>
        )
      },
      meta: {
        className: 'w-32',
      },
    },
    {
      id: 'actions',
      header: t('common.actions'),
      cell: ({ row }) => <FnsActionsCell row={row} />,
      meta: {
        className: 'w-20',
      },
    },
  ]

  // Conditionally include type column based on mode
  return mode === 'survey'
    ? [...baseColumns, typeColumn, ...remainingColumns]
    : [...baseColumns, ...remainingColumns]
}
